import type { Role } from "./enums";

export const PERMISSIONS = [
  "dashboard.view",
  "orders.view",
  "orders.manage",
  "orders.export",
  "customers.view",
  "customers.manage",
  "products.view",
  "products.manage",
  "shipping.view",
  "shipping.manage",
  "returns.manage",
  "payments.view",
  "payments.manage",
  "conversations.view",
  "conversations.reply",
  "conversations.manage",
  "followups.manage",
  "playbooks.manage",
  "markets.manage",
  "ads.view",
  "ads.manage",
  "ads.execute",
  "ads.approve",
  "ai.use",
  "ai.configure",
  "sites.view",
  "sites.manage",
  "analytics.view",
  "reports.manage",
  "integrations.manage",
  "settings.manage",
  "members.manage",
  "org.manage",
  "billing.manage",
  "audit.view",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const VIEW_ONLY: Permission[] = PERMISSIONS.filter((p) => p.endsWith(".view"));

const OPERATIONS: Permission[] = [
  "dashboard.view",
  "orders.view",
  "orders.manage",
  "orders.export",
  "customers.view",
  "customers.manage",
  "products.view",
  "products.manage",
  "shipping.view",
  "shipping.manage",
  "returns.manage",
  "payments.view",
  "payments.manage",
  "conversations.view",
  "conversations.reply",
  "conversations.manage",
  "followups.manage",
  "playbooks.manage",
  "markets.manage",
  "ads.view",
  "ads.manage",
  "ads.approve",
  "ai.use",
  "sites.view",
  "sites.manage",
  "analytics.view",
  "reports.manage",
  "audit.view",
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: PERMISSIONS,
  admin: PERMISSIONS.filter((p) => p !== "billing.manage"),
  manager: OPERATIONS,
  media_buyer: [
    "dashboard.view",
    "orders.view",
    "customers.view",
    "products.view",
    "shipping.view",
    "payments.view",
    "ads.view",
    "ads.manage",
    "ads.execute",
    "ads.approve",
    "ai.use",
    "sites.view",
    "sites.manage",
    "analytics.view",
    "reports.manage",
    "audit.view",
  ],
  agent: [
    "dashboard.view",
    "orders.view",
    "orders.manage",
    "customers.view",
    "customers.manage",
    "products.view",
    "shipping.view",
    "shipping.manage",
    "conversations.view",
    "conversations.reply",
    "ai.use",
  ],
  viewer: VIEW_ONLY,
};

export function permissionsFor(role: Role): Set<Permission> {
  return new Set(ROLE_PERMISSIONS[role]);
}

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Roles a member with `role` may assign to others (never above their own rank). */
export function assignableRoles(role: Role): Role[] {
  const rank: Record<Role, number> = { owner: 5, admin: 4, manager: 3, media_buyer: 2, agent: 2, viewer: 1 };
  if (role === "owner") return ["admin", "manager", "media_buyer", "agent", "viewer"];
  if (role === "admin") return ["manager", "media_buyer", "agent", "viewer"];
  return (Object.keys(rank) as Role[]).filter((r) => rank[r] < rank[role] && r !== "owner");
}
